import { useEffect, useMemo, useRef, useState } from 'react';
import { MultiSelect, RegistrationModalShell, SingleRangeSlider, TuitionModeSelector, WeeklyTimeTable } from '@tutor/ui';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { STATE_DISTRICTS, STATE_OPTIONS } from '@tutor/types';
import { getErrorMessage } from '@/api/errorHandler';
import { getMusicTutorProfile, submitMusicTutorProfile, updateMusicTutorProfile } from '@/api/services/musicProfilesApi';
import { updateTutorProfile } from '@/api/services/profilesApi';
import { TUITION_EXPERIENCE_YEARS } from '@/constants/dropdownOptions';
import { useAuthContext } from '@/contexts/useAuthContext';
import { useModal } from '@/contexts/useModal';
import { clearPendingRegistration, getPendingRegistration, savePendingRegistration } from '@/utils/storage';
import { parseMusicTutorStep } from '@/schemas/musicRegistrationSchemas';
import type { MusicPortfolioItemInput, MusicQualificationInput, MusicTutorRegistrationFormData } from '@/types/musicRegistration';
import {
  MUSIC_AGE_GROUPS, MUSIC_EXAM_BOARDS, MUSIC_LESSON_SUBJECTS, MUSIC_LESSON_FORMATS,
  MUSIC_LEVELS, MUSIC_PHYSICAL_VENUES, MUSIC_PORTFOLIO_TYPES, MUSIC_QUALIFICATION_TYPES, MUSIC_TEACHING_LANGUAGES,
} from '@/constants/musicRegistrationOptions';
import { CheckboxList, DropdownInput, Field, StepGrid, TextArea, TextInput } from './MusicFormControls';
import { useNavigate } from 'react-router-dom';
import { useOptionalI18n } from '@/i18n';
import { localizeOptions, localizeStringOptions, localizeText } from '@/i18n/options';
import { getWeeklyTimeTableI18nProps } from '@/utils/weeklyTimeTableI18n';
import { completeMusicRegistrationSession } from '@/utils/musicRegistrationWorkflow';
import { useLocalizedPath } from '@/i18n/navigation';

const steps = [
  { id: 'setup', label: 'Lesson setup' },
  { id: 'teaching', label: 'Teaching scope' },
  { id: 'qualifications', label: 'Professional qualifications' },
  { id: 'profile', label: 'Profile & review' },
];

const createEmptyQualification = (): MusicQualificationInput => ({ type: MUSIC_QUALIFICATION_TYPES[0], title: '' });
const createEmptyPortfolioItem = (): MusicPortfolioItemInput => ({ type: MUSIC_PORTFOLIO_TYPES[0], title: '', url: '' });

const initialData: MusicTutorRegistrationFormData = {
  instruments: [], musicLevels: [], ageGroups: [], tuitionType: '', lessonFormats: [], physicalVenues: [],
  preferredState: '', preferredDistricts: [], availability: {}, teachingExperienceYears: '', examBoards: [],
  qualifications: [], portfolioItems: [], teachingLanguages: [], hourlyRate: 60, aboutMe: '', agreedToTerms: false,
};

const getErrors = (result: ReturnType<typeof parseMusicTutorStep>, locale: Parameters<typeof localizeText>[0]) => {
  if (result.success) return {};
  const fieldErrors: Record<string, string> = {};
  result.error.issues.forEach((issue) => {
    const field = String(issue.path[0]);
    if (field && !fieldErrors[field]) fieldErrors[field] = localizeText(locale, issue.message);
  });
  return fieldErrors;
};

interface MusicTutorRegistrationProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  presentation?: 'modal' | 'page';
  showPageBackButton?: boolean;
  successFeedback?: 'toast' | 'none';
}

const MusicTutorRegistrationModal = ({
  isOpen,
  onClose,
  onCompleted,
  presentation = 'modal',
  showPageBackButton,
  successFeedback = 'toast',
}: MusicTutorRegistrationProps) => {
  const navigate = useNavigate();
  const localizePath = useLocalizedPath();
  const { locale } = useOptionalI18n();
  const l = (text: string) => localizeText(locale, text);
  const weeklyTimeTableI18n = getWeeklyTimeTableI18nProps(locale);
  const lessonFormats = localizeOptions(locale, MUSIC_LESSON_FORMATS);
  const physicalVenues = localizeOptions(locale, MUSIC_PHYSICAL_VENUES);
  const stateOptions = localizeOptions(locale, STATE_OPTIONS);
  const instruments = localizeStringOptions(locale, MUSIC_LESSON_SUBJECTS);
  const musicLevels = localizeStringOptions(locale, MUSIC_LEVELS);
  const ageGroups = localizeStringOptions(locale, MUSIC_AGE_GROUPS);
  const teachingLanguages = localizeStringOptions(locale, MUSIC_TEACHING_LANGUAGES);
  const { backendUser, profile, updateProfile } = useAuthContext();
  const { openLoginModal, openCloseWarning } = useModal();
  const [data, setData] = useState(initialData);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const errorsRef = useRef<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldResumeSubmission, setShouldResumeSubmission] = useState(false);
  const isDirty = useMemo(() => JSON.stringify(data) !== JSON.stringify(initialData), [data]);
  const update = (values: Partial<MusicTutorRegistrationFormData>) => setData((current) => ({ ...current, ...values }));

  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  // Match Academic registration: only refresh errors already shown, and remove
  // each one as soon as that field becomes valid without revealing new errors.
  useEffect(() => {
    const currentErrors = errorsRef.current;
    if (Object.keys(currentErrors).length === 0) return;

    const result = parseMusicTutorStep(currentStep, data);
    const failingFields = getErrors(result, locale);
    const refreshedErrors = Object.fromEntries(
      Object.keys(currentErrors)
        .filter((field) => failingFields[field])
        .map((field) => [field, failingFields[field]]),
    );

    if (JSON.stringify(refreshedErrors) !== JSON.stringify(currentErrors)) {
      setErrors(refreshedErrors);
    }
  }, [currentStep, data, locale]);

  useEffect(() => {
    if (!isOpen) return;
    const pending = getPendingRegistration();
    if (pending?.type === 'music_tutor') {
      setData({
        ...initialData,
        ...pending.formData,
        examBoards: (pending.formData.examBoards as string[] | undefined)?.filter((examBoard) => examBoard.trim()) ?? [],
      } as MusicTutorRegistrationFormData);
      setCurrentStep(steps.length - 1);
      setShouldResumeSubmission(true);
      return;
    }
    if (!backendUser || profile?.role !== 'tutor') return;
    getMusicTutorProfile().then((music) => {
      if (!music) return;
      setData((current) => ({
        ...current,
        instruments: music.instruments ?? [], musicLevels: music.musicLevels ?? [], ageGroups: music.ageGroups ?? [],
        lessonFormats: music.lessonFormats ?? [], physicalVenues: music.physicalVenues ?? [], examBoards: music.examBoards?.filter((examBoard) => examBoard.trim()) ?? [],
        qualifications: music.qualifications ?? [], portfolioItems: music.portfolioItems ?? [],
        teachingExperienceYears: music.teachingExperienceYears ?? '', hourlyRate: music.hourlyRate ?? current.hourlyRate,
        tuitionType: profile.tuitionType ?? current.tuitionType, preferredState: profile.preferredState ?? '',
        preferredDistricts: profile.preferredDistricts ?? [], availability: profile.availability ?? {},
        teachingLanguages: profile.teachingLanguages ?? [], aboutMe: profile.aboutMe ?? '',
      }));
    }).catch(() => undefined);
  }, [backendUser, isOpen, profile]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!isOpen) {
      window.__devPanel?.unregister('music_tutor');
      return;
    }
    window.__devPanel?.register({
      type: 'music_tutor',
      setFormData: (value) => setData(value as MusicTutorRegistrationFormData),
      setCurrentStep,
      stepCount: steps.length,
      get currentStep() { return currentStep; },
    });
    return () => window.__devPanel?.unregister('music_tutor');
  }, [currentStep, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      window.__debugBridge?.unregisterFormData('music_tutor');
      return;
    }
    window.__debugBridge?.registerFormData('music_tutor', () => ({
      formData: data as unknown as Record<string, unknown>,
      validationErrors: errors,
      currentStep,
      stepCount: steps.length,
    }));
    return () => window.__debugBridge?.unregisterFormData('music_tutor');
  }, [currentStep, data, errors, isOpen]);

  const validate = () => {
    const result = parseMusicTutorStep(currentStep, data);
    setErrors(getErrors(result, locale));
    return result.success;
  };

  const safeClose = () => {
    if (isDirty && !isSubmitting) {
      openCloseWarning({ title: l('Unsaved music profile'), message: l('Discard the information entered in this form?'), cancelText: l('Continue editing'), confirmText: l('Discard'), onConfirm: onClose });
      return;
    }
    onClose();
  };

  const submit = async () => {
    if (!validate()) return;
    if (backendUser && (profile?.role ?? backendUser.currentRole) !== 'tutor') {
      toast.error(l('Please switch to a Tutor account before submitting.'));
      return;
    }
    if (!backendUser) {
      savePendingRegistration('music_tutor', data as unknown as Record<string, unknown>, presentation);
      if (presentation === 'page') {
        navigate('/music/register/auth');
        return;
      }
      onClose();
      openLoginModal({ initialMode: 'signup', initialUserType: 'tutor', fromRegistration: true, registrationService: 'music' });
      return;
    }
    setIsSubmitting(true);
    try {
      const qualifications = data.qualifications
        .filter((qualification) => qualification.title.trim())
        .map((qualification) => ({ ...qualification, title: qualification.title.trim() }));
      const portfolioItems = data.portfolioItems
        .filter((portfolioItem) => portfolioItem.title.trim())
        .map(({ url, instrument, ...portfolioItem }) => ({
          ...portfolioItem,
          title: portfolioItem.title.trim(),
          ...(url?.trim() ? { url: url.trim() } : {}),
          ...(instrument?.trim() ? { instrument: instrument.trim() } : {}),
        }));
      const baseProfile = await updateTutorProfile({
        tuitionType: data.tuitionType as 'physical' | 'online' | 'both',
        preferredState: data.tuitionType === 'online' ? undefined : data.preferredState,
        preferredDistricts: data.tuitionType === 'online' ? [] : data.preferredDistricts,
        availability: data.availability, teachingLanguages: data.teachingLanguages,
        aboutMe: data.aboutMe, agreedToTerms: data.agreedToTerms,
      });
      await updateMusicTutorProfile({
        instruments: data.instruments, musicLevels: data.musicLevels, ageGroups: data.ageGroups,
        lessonFormats: data.lessonFormats,
        physicalVenues: data.tuitionType === 'online' ? [] : data.physicalVenues,
        examBoards: data.examBoards.map((examBoard) => examBoard.trim()).filter(Boolean),
        qualifications, portfolioItems,
        teachingExperienceYears: Number(data.teachingExperienceYears), hourlyRate: data.hourlyRate,
      });
      await submitMusicTutorProfile();
      updateProfile({ ...baseProfile, role: 'tutor' });
      clearPendingRegistration();
      if (successFeedback === 'toast') {
        toast.success(l('Your Music Tutor profile has been submitted for review.'));
      }
      setData(initialData);
      setCurrentStep(0);
      await completeMusicRegistrationSession();
      await (onCompleted ?? onClose)();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const activeRole = profile?.role ?? backendUser?.currentRole;
    if (!shouldResumeSubmission || !backendUser || activeRole !== 'tutor' || isSubmitting) return;
    setShouldResumeSubmission(false);
    void submit();
  // The resume flag guarantees this runs once after authenticated state and restored data are ready.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUser, data, isSubmitting, profile, shouldResumeSubmission]);

  const qualificationItems = data.qualifications.length ? data.qualifications : [createEmptyQualification()];
  const portfolioItems = data.portfolioItems.length ? data.portfolioItems : [createEmptyPortfolioItem()];
  const addQualification = () => update({ qualifications: [...qualificationItems, createEmptyQualification()] });
  const addPortfolio = () => update({ portfolioItems: [...portfolioItems, createEmptyPortfolioItem()] });
  return (
    <RegistrationModalShell isOpen={isOpen} onClose={safeClose} title={l('Become a Music Tutor')} steps={steps.map((step) => ({ ...step, label: l(step.label) }))} currentStep={currentStep} onBack={() => { setErrors({}); setCurrentStep((step) => Math.max(0, step - 1)); }} onNext={() => { if (validate()) { setErrors({}); setCurrentStep((step) => step + 1); } }} onSubmit={submit} isSubmitting={isSubmitting} submitLabel={l('Submit for review')} backLabel={l('Back')} continueLabel={l('Continue')} pageBackLabel={l('Registration home')} themeClassName="theme-music" presentation={presentation} showPageBackButton={showPageBackButton}>
      {currentStep === 0 && <div className="space-y-6">
        <Field label={l('Lesson mode')} error={errors.tuitionType}><TuitionModeSelector value={data.tuitionType} onChange={(tuitionType) => update({ tuitionType })} labels={{ physical: l('Physical Tuition'), online: l('Online Tuition'), both: l('Both'), selected: l('Selected'), physicalHint: l('98.3% of parents choose this') }} /></Field>
        <Field label={l('Lesson formats')} error={errors.lessonFormats}><CheckboxList options={lessonFormats} value={data.lessonFormats} onChange={(values) => update({ lessonFormats: values as MusicTutorRegistrationFormData['lessonFormats'] })} /></Field>
        {data.tuitionType !== 'online' && <Field label={l('Physical venues (optional)')} hint={l('Select any locations you can offer, or leave blank.')}><CheckboxList options={physicalVenues} value={data.physicalVenues} onChange={(physicalVenues) => update({ physicalVenues: physicalVenues as MusicTutorRegistrationFormData['physicalVenues'] })} /></Field>}
        {data.tuitionType !== 'online' && <section className="space-y-5 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900">{l('Teaching location')}</h3>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label={l('Preferred state')} error={errors.preferredState}><DropdownInput options={stateOptions} value={data.preferredState} onChange={(value) => update({ preferredState: String(value), preferredDistricts: [] })} placeholder={l('Select state')} /></Field>
            <Field label={l('Preferred districts')} error={errors.preferredDistricts}><MultiSelect disabled={!data.preferredState} options={localizeStringOptions(locale, STATE_DISTRICTS[data.preferredState as keyof typeof STATE_DISTRICTS] ?? [])} value={data.preferredDistricts} onChange={(preferredDistricts) => update({ preferredDistricts })} /></Field>
          </div>
        </section>}
      </div>}
      {currentStep === 1 && <div className="space-y-6">
        <StepGrid>
          <Field label={l('Instruments you teach')} error={errors.instruments}><MultiSelect options={instruments} value={data.instruments} onChange={(instruments) => update({ instruments })} allowCustom /></Field>
          <Field label={l('Music levels')} error={errors.musicLevels}><MultiSelect options={musicLevels} value={data.musicLevels} onChange={(musicLevels) => update({ musicLevels })} allowCustom /></Field>
          <Field label={l('Student age groups')} error={errors.ageGroups}><MultiSelect options={ageGroups} value={data.ageGroups} onChange={(ageGroups) => update({ ageGroups })} allowCustom /></Field>
          <Field label={l('Music exam boards you can teach (optional)')} error={errors.examBoards}><MultiSelect options={localizeStringOptions(locale, MUSIC_EXAM_BOARDS)} value={data.examBoards} onChange={(examBoards) => update({ examBoards })} allowCustom /></Field>
        </StepGrid>
        <div><WeeklyTimeTable selectedTimes={data.availability} onChange={(availability) => update({ availability })} label={l('Teaching availability')} helperText={l('Click on the grid to select your preferred teaching times')} {...weeklyTimeTableI18n} />{errors.availability && <p role="alert" className="mt-2 text-sm font-medium text-red-600">{errors.availability}</p>}</div>
      </div>}
      {currentStep === 2 && <div className="space-y-6">
        <Field label={l('Years teaching music')} error={errors.teachingExperienceYears}><DropdownInput searchable={false} options={localizeOptions(locale, TUITION_EXPERIENCE_YEARS.map((option) => ({ value: option.value, label: option.label })))} value={data.teachingExperienceYears === '' ? '' : data.teachingExperienceYears} onChange={(value) => update({ teachingExperienceYears: Number(value) })} placeholder={l('Select years of experience')} /></Field>
        <div className="flex items-center justify-between"><h3 className="font-semibold text-gray-900">{l('Qualifications (optional)')}</h3><button type="button" onClick={addQualification} className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-primary-50"><Plus className="h-4 w-4" /> {l('Add')}</button></div>
        {qualificationItems.map((item, index) => <div key={index} className="relative rounded-xl border border-gray-200 p-4"><button type="button" aria-label={l('Remove qualification')} onClick={() => update({ qualifications: qualificationItems.length === 1 ? [] : qualificationItems.filter((_, itemIndex) => itemIndex !== index) })} className="absolute right-3 top-3 min-h-11 min-w-11 text-red-600"><Trash2 className="mx-auto h-4 w-4" /></button><div className="grid gap-4 pr-10 md:grid-cols-2"><DropdownInput searchable={false} options={localizeStringOptions(locale, MUSIC_QUALIFICATION_TYPES)} value={item.type} onChange={(value) => update({ qualifications: qualificationItems.map((qualification, itemIndex) => itemIndex === index ? { ...qualification, type: String(value) } : qualification) })} /><TextInput placeholder={l('Qualification title')} value={item.title} onChange={(event) => update({ qualifications: qualificationItems.map((qualification, itemIndex) => itemIndex === index ? { ...qualification, title: event.target.value } : qualification) })} /></div></div>)}
        {errors.qualifications && <p role="alert" className="text-sm font-medium text-red-600">{errors.qualifications}</p>}
        <div className="flex items-center justify-between"><div><h3 className="font-semibold text-gray-900">{l('Professional portfolio (optional)')}</h3><p className="text-sm text-gray-600">{l('Add YouTube, audio, or professional profile links.')}</p></div><button type="button" onClick={addPortfolio} className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-primary-50"><Plus className="h-4 w-4" /> {l('Add')}</button></div>
        {portfolioItems.map((item, index) => <div key={index} className="relative rounded-xl border border-gray-200 p-4"><button type="button" aria-label={l('Remove portfolio item')} onClick={() => update({ portfolioItems: portfolioItems.length === 1 ? [] : portfolioItems.filter((_, itemIndex) => itemIndex !== index) })} className="absolute right-3 top-3 min-h-11 min-w-11 text-red-600"><Trash2 className="mx-auto h-4 w-4" /></button><div className="grid gap-4 pr-10 md:grid-cols-2"><DropdownInput searchable={false} options={localizeStringOptions(locale, MUSIC_PORTFOLIO_TYPES)} value={item.type} onChange={(value) => update({ portfolioItems: portfolioItems.map((portfolioItem, itemIndex) => itemIndex === index ? { ...portfolioItem, type: String(value) } : portfolioItem) })} /><TextInput placeholder={l('Title')} value={item.title} onChange={(event) => update({ portfolioItems: portfolioItems.map((portfolioItem, itemIndex) => itemIndex === index ? { ...portfolioItem, title: event.target.value } : portfolioItem) })} /><TextInput type="url" placeholder="https://" value={item.url ?? ''} onChange={(event) => update({ portfolioItems: portfolioItems.map((portfolioItem, itemIndex) => itemIndex === index ? { ...portfolioItem, url: event.target.value } : portfolioItem) })} /><TextInput placeholder={l('Instrument (optional)')} value={item.instrument ?? ''} onChange={(event) => update({ portfolioItems: portfolioItems.map((portfolioItem, itemIndex) => itemIndex === index ? { ...portfolioItem, instrument: event.target.value } : portfolioItem) })} /></div></div>)}
        {errors.portfolioItems && <p role="alert" className="text-sm font-medium text-red-600">{errors.portfolioItems}</p>}
      </div>}
      {currentStep === 3 && <div className="space-y-6">
        <Field label={l('Teaching languages')} error={errors.teachingLanguages}><MultiSelect searchable={false} options={teachingLanguages} value={data.teachingLanguages} onChange={(teachingLanguages) => update({ teachingLanguages })} /></Field>
        <div className="space-y-2 rounded-xl border border-primary-200 bg-primary-50/70 p-4"><div className="flex min-h-7 items-center justify-between gap-3"><span className="text-sm font-semibold text-primary-800">{l('Hourly rate')} <span className="text-red-500">*</span></span><a href={localizePath('/music/price')} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-md text-sm font-semibold text-primary-700 underline underline-offset-4 hover:text-primary-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">{l('View music rate guide')}</a></div><SingleRangeSlider min={10} max={800} value={data.hourlyRate} onChange={(hourlyRate) => update({ hourlyRate })} unit="RM" colorTheme="primary" />{errors.hourlyRate && <p role="alert" className="text-sm font-medium text-red-600">{errors.hourlyRate}</p>}</div>
        <Field label={l('About your music teaching')} error={errors.aboutMe} hint={l('Describe your approach, background, and what students can expect.')}><TextArea rows={5} maxLength={2000} value={data.aboutMe} onChange={(event) => update({ aboutMe: event.target.value })} /></Field>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-300 p-4"><input type="checkbox" checked={data.agreedToTerms} onChange={(event) => update({ agreedToTerms: event.target.checked })} className="mt-1 h-4 w-4 accent-primary" /><span className="text-sm text-gray-700">{l('I agree to the')} <a href="/terms-tutors" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline hover:text-primary-600">{l('Tutor Terms of Service')}</a> {l('and')} <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline hover:text-primary-600">{l('Privacy Policy')}</a>{l('.')}</span></label>{errors.agreedToTerms && <p role="alert" className="text-sm text-red-600">{errors.agreedToTerms}</p>}
      </div>}
    </RegistrationModalShell>
  );
};

export default MusicTutorRegistrationModal;
